import Button from "@cloudscape-design/components/button"
import Tooltip from "@cloudscape-design/components/tooltip"
import { useRef, useState } from "react"

interface InlineActionButtonProps {
  /**
   * The action label.
   */
  children: string
  /**
   * Whether the action is disabled.
   */
  disabled?: boolean
  /**
   * Reason shown in a tooltip (on hover/focus) when `disabled` is true.
   *
   * Cloudscape's own `Button` only renders the `disabledReason` tooltip for the `normal`,
   * `primary`, and `icon` variants - it silently ignores it for `link`/`inline-link` (see
   * button/internal.js `isDisabledWithReason`). This wrapper restores that affordance for
   * inline-link actions.
   */
  disabledReason?: string
  /**
   * Called when the (enabled) action is clicked.
   */
  onClick: () => void
}

/**
 * An `inline-link` Button that surfaces its `disabledReason` as a hover/focus tooltip, since
 * Cloudscape's Button does not render `disabledReason` tooltips for the inline-link variant.
 *
 * The disabled button renders with `pointer-events: none`, so hover events fall through to the
 * wrapping span; the span is made focusable when disabled so keyboard users can reveal the reason
 * too.
 */
const InlineActionButton = ({
  children,
  disabled = false,
  disabledReason,
  onClick,
}: InlineActionButtonProps) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  const showReason = disabled && !!disabledReason

  if (!disabled) {
    return (
      <Button variant="inline-link" onClick={onClick}>
        {children}
      </Button>
    )
  }

  return (
    <span
      ref={ref}
      style={{ display: "inline-flex" }}
      tabIndex={showReason ? 0 : undefined}
      aria-disabled="true"
      aria-label={showReason ? `${children} (disabled): ${disabledReason}` : undefined}
      onMouseEnter={showReason ? () => setShowTooltip(true) : undefined}
      onMouseLeave={showReason ? () => setShowTooltip(false) : undefined}
      onFocus={showReason ? () => setShowTooltip(true) : undefined}
      onBlur={showReason ? () => setShowTooltip(false) : undefined}
    >
      <Button variant="inline-link" disabled>
        {children}
      </Button>
      {showReason && showTooltip && (
        <Tooltip
          getTrack={() => ref.current}
          content={disabledReason}
          onEscape={() => setShowTooltip(false)}
        />
      )}
    </span>
  )
}

export default InlineActionButton
